import { useBoolean } from 'ahooks';
import { Form } from 'antd';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from 'react-query';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { UserAPI } from 'api/user';
import { Button } from 'components/atoms/button';
import {
  ErrorMessage,
  LinkText,
  UserInfo,
} from 'components/atoms/form-elements';
import { Input } from 'components/atoms/input';
import {
  Container,
  Heading,
  HeadingContainer,
  HelperTitle,
  TextContainer,
  WelcomeHeading,
} from 'components/organisms/login';
import { RoutePaths } from 'constant';
import { useTranslation } from 'hooks/use-translation';
import { useAccountMetaQuery } from 'queries';
import { CompanyLogo } from 'routes/auth/common/company-logo';
import { SuccessPage } from './customer-login-success';

const RecoveryButton = styled(Button)`
  margin: var(--spacing-03) 0;
  width: 100%;
  background: var(--blue-70);
`;

const StyledForm = styled(Form)`
  width: 400px;
`;

export const CustomerLogin = () => {
  const [form] = Form.useForm();
  const [email, setEmail] = useState('');
  const request = (_email: string) => UserAPI.getMagicLink(_email);
  const [isSuccess, { setTrue }] = useBoolean(false);
  const {
    isLoading: loading,
    mutateAsync: handleClick,
    isError: isEmailSentFailed,
  } = useMutation(request, {
    onSuccess: () => {
      setTrue();
    },
  });

  const onSubmit = useCallback(async () => {
    const { email: _email } = await form.validateFields();

    setEmail(_email);
    handleClick(_email);
  }, [form, handleClick]);

  const translatedPlaceholderText = useTranslation('forgot_password.email');
  const requiredText = useTranslation('forgot_password.email_required');

  const { data } = useAccountMetaQuery();

  if (isSuccess) {
    return <SuccessPage email={email} />;
  }

  return (
    <Container>
      <HeadingContainer>
        {data && <CompanyLogo data={data.company} />}
        <TextContainer>
          <WelcomeHeading>
            <FormattedMessage id="welcome" />
          </WelcomeHeading>
          <Heading>
            <FormattedMessage
              id="login.title"
              values={{
                companyName: data?.company.companyName,
              }}
            />
          </Heading>
        </TextContainer>
      </HeadingContainer>

      <HelperTitle>
        <FormattedMessage id="sent_email_link_to_access_portal" />
      </HelperTitle>
      <StyledForm
        form={form}
        layout="vertical"
        onSubmitCapture={onSubmit}
        onFinish={onSubmit}
      >
        <Form.Item
          name="email"
          label={translatedPlaceholderText}
          rules={[
            {
              required: true,
              message: requiredText,
            },
          ]}
        >
          <Input type="email" placeholder={translatedPlaceholderText} />
        </Form.Item>
        <ErrorMessage>
          {isEmailSentFailed ? 'Something went wrong. Please try again.' : ''}
        </ErrorMessage>
        <RecoveryButton
          loading={loading}
          type="primary"
          onClick={onSubmit}
          size="large"
        >
          <FormattedMessage id="email_signin_link" />
        </RecoveryButton>
      </StyledForm>
      <UserInfo>
        <FormattedMessage
          id="forgot_password.cta_back"
          values={{
            a: (chunks) => (
              <Link to={RoutePaths.login}>
                <LinkText>{chunks}</LinkText>
              </Link>
            ),
          }}
        />
      </UserInfo>
    </Container>
  );
};
